import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePreGame, useActiveGame, useGames, useAppConfig, useAdminUsers } from '@/hooks/useShared';
import { useOutstandingDebt } from '@/hooks/useOutstandingDebt';
import { GameScreen } from '@/pages/GameScreen';
import { AuthPage } from '@/pages/AuthPage';
import { SetupScreen } from '@/pages/SetupScreen';
import { SettlementPage } from '@/pages/SettlementPage';
import { GameSettledPage } from '@/pages/GameSettledPage';
import { DesignSandbox } from '@/pages/DesignSandbox';
import NotificationService from '@/services/notification.service';

import { LibraryPage } from '@/pages/LibraryPage';
import { LeaderboardPage } from '@/pages/LeaderboardPage';
import { GameStatsPage } from '@/pages/GameStatsPage';
import { PlayerDetailPage } from '@/pages/PlayerDetailPage';
import { VenueStatsPage } from '@/pages/VenueStatsPage';
import { DebtsPage } from '@/pages/DebtsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { DataExplorerPage } from '@/pages/DataExplorerPage';
import { EntertainmentPage } from '@/pages/EntertainmentPage';
import { TelegramTestPage } from '@/pages/TelegramTestPage';
import { ChipScannerPage } from '@/pages/ChipScannerPage';
import FeedbackReviewPage from '@/pages/admin/FeedbackReviewPage';
import { AdminUsersPage } from '@/components/admin/AdminUsersPage';
import { AdminUserDetailPage } from '@/components/admin/AdminUserDetailPage';
import {
  AccountConflictPage,
  AccountDisabledPage,
  AccountPendingPage,
  AccountSecurityPage,
  AccountSetupPage,
} from '@/pages/account/AccountStatePages';
import NewGamePage from '@/pages/NewGamePage';
import EditGamePage from '@/pages/EditGamePage';
import { Toaster } from 'sonner';
import { LoadingSpinner } from '@/components/ui';
import { runtimeEnv } from '@/lib/runtime-env';
import type { Game } from '@/lib/types';
import { MVP_FRICTIONLESS } from '@/hooks/useAuth';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { UserProfileProvider, useUserProfile } from '@/contexts/UserProfileContext';
import { canManageUsers } from '@/lib/roles';
import FeedbackBubble from '@/components/feedback/FeedbackBubble';

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="min-h-dvh bg-aura-background text-aura-text font-manrope">
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/account/setup" element={<AccountSetupPage />} />
          <Route path="/account/security" element={<AccountSecurityPage />} />
          <Route path="/account/pending" element={<AccountPendingPage />} />
          <Route path="/account/conflict" element={<AccountConflictPage />} />
          <Route path="/account/disabled" element={<AccountDisabledPage />} />
          <Route path="/design-sandbox" element={<DesignSandbox />} />
          <Route path="/*" element={<MainLayout />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

function MainLayout() {
  const { firebaseUser, user, loading } = useAuth();
  const { loading: preGameLoading } = usePreGame();
  const { activeGame, loading: activeGameLoading } = useActiveGame();
  const gameLoading = preGameLoading || activeGameLoading;
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { needRefresh, updateServiceWorker } = useRegisterSW();
  const userCanManageUsers = canManageUsers(user);

  // Local state to track if the user has acknowledged the refresh in this session
  const [hasClickedUpdate, setHasClickedUpdate] = useState(() => {
    return localStorage.getItem('pwa_update_acknowledged') === 'true';
  });

   // Auto-redirect: handled by GameScreen internally
   const navigate = useNavigate();
  useEffect(() => {
    NotificationService.startRetryLoop();
    NotificationService.processOutbox().catch(console.error);
    
    return () => {
      // retry loop persists across mounts intentionally
    };
  }, []);
   useEffect(() => {
     // Removed manual redirects to /game and /settlement as GameScreen handles status-based rendering
   }, [activeGame, activeGame?.status, location.pathname, navigate]);

  console.log('MainLayout:', { loading, firebaseUser: !!firebaseUser, user: !!user, gameLoading, activeGame: !!activeGame });

  if (loading) {
    return (
        <div className="flex items-center justify-center min-h-dvh">
        <LoadingSpinner message="Loading Poker Knights..." />
      </div>
    );
  }

  if (!MVP_FRICTIONLESS && !firebaseUser) {
    return <Navigate to="/auth" replace />;
  }

  if (gameLoading) {
    return (
        <div className="flex items-center justify-center min-h-dvh">
        <LoadingSpinner message="Loading Poker Knights..." />
      </div>
    );
  }

  // Ensure Spectator or specific userId bypasses access pending
  if (!user && !MVP_FRICTIONLESS) {
    console.log('MainLayout: user is null, showing Access Pending');
    return (
      <div className="p-6 text-center pt-20">
        <div className="text-6xl mb-6">🔒</div>
        <h2 className="text-aura-text text-xl font-black mb-3">Access Pending</h2>
        <p className="text-aura-text/60">
          Ask an admin to add you to the group.
        </p>
      </div>
    );
  }

    const appContent = (
      <div className="w-full flex justify-center bg-[#f5f7f8] min-h-dvh">
        <div className="w-full min-w-0 max-w-[400px] sm:max-w-[95vw] flex flex-col h-dvh bg-white shadow-botanical-lg relative">
          <TopNav isAdmin={userCanManageUsers} onMenuClick={() => setMenuOpen(true)} userId={user?.id} />

          {/* Main Content Area - Layout Flow */}
          <main className="flex-1 overflow-y-auto px-4 sm:px-6 md:px-8">

          <Routes>
            <Route path="/" element={<Navigate to="/setup" replace />} />
            <Route path="/setup" element={<SetupScreen />} />
            <Route path="/setup/new" element={<NewGamePage />} />
            <Route path="/setup/edit/:gameId" element={<EditGamePage />} />
            <Route path="/game" element={<GameScreen />} />
            <Route path="/game/:gameId" element={<GameScreen />} />
            <Route path="/settlement/:gameId" element={<SettlementPage />} />
            <Route path="/game-settled" element={<GameSettledPage />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route path="/leaderboard" element={<Navigate to="/insights/leaderboard" replace />} />
            <Route path="/insights/leaderboard" element={<LeaderboardPage />} />
            <Route path="/insights/games" element={<GameStatsPage />} />
            <Route path="/insights/players/:userId" element={<PlayerDetailPage />} />
            <Route path="/insights/venues" element={<VenueStatsPage />} />
            <Route path="/insights/debts" element={<DebtsPage />} />
            <Route path="/settings" element={<AdminOnlyRoute allowed={userCanManageUsers}><SettingsPage /></AdminOnlyRoute>} />
            <Route path="/admin/users/:userId" element={<AdminOnlyRoute allowed={userCanManageUsers}><AdminUserDetailRoute /></AdminOnlyRoute>} />
            <Route path="/admin/users" element={<AdminOnlyRoute allowed={userCanManageUsers}><AdminUsersRoute /></AdminOnlyRoute>} />
            <Route path="/profile" element={<ProfilePage />} />
             <Route path="/chip-scanner" element={<ChipScannerPage />} />
             <Route path="/explorer" element={<AdminOnlyRoute allowed={userCanManageUsers}><DataExplorerPage /></AdminOnlyRoute>} />
             <Route path="/admin/feedback" element={<AdminOnlyRoute allowed={userCanManageUsers}><FeedbackReviewPage /></AdminOnlyRoute>} />
             <Route path="/entertainment" element={<EntertainmentPage />} />
             <Route path="/telegram-test" element={<AdminOnlyRoute allowed={userCanManageUsers}><TelegramTestPage /></AdminOnlyRoute>} />

          </Routes>
          <Toaster />
        </main>

        <BottomTabBar activeGame={activeGame} />
        
        {/* PWA Update Toast */}
        {needRefresh && !hasClickedUpdate && (
          <div 
            onClick={() => {
              setHasClickedUpdate(true);
              localStorage.setItem('pwa_update_acknowledged', 'true');
              updateServiceWorker(true);
              window.location.reload();
            }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2 w-[90%] max-w-[360px] z-[300] cursor-pointer transition-all duration-300 hover:scale-[1.02] active:scale-95 animate-in fade-in slide-in-from-bottom-4"
          >
            <div className="bg-gradient-to-r from-aura-primary to-aura-secondary text-white px-5 py-4 rounded-3xl shadow-[0_20px_50px_rgba(0,104,85,0.3)] flex items-center gap-3 border border-white/20 backdrop-blur-md bg-opacity-90">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-lg animate-pulse">
                ✨
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-black tracking-tight leading-tight">New version available!</span>
                <span className="text-[10px] font-medium opacity-80">Click to refresh app</span>
              </div>
            </div>
          </div>
        )}
        <FeedbackBubble isDrawerOpen={menuOpen} />
      </div>
      <Drawer isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
    </div>
    );

    if (user?.id) {
      return (
        <UserProfileProvider userId={user.id}>
          {appContent}
        </UserProfileProvider>
      );
    }

    return appContent;
}

function TopNav({ isAdmin, onMenuClick, userId }: { isAdmin?: boolean; onMenuClick: () => void; userId?: string }) {
  const { hasDebt, totalOwed } = useOutstandingDebt(userId);
  return (
    <nav className="min-h-[60px] bg-white border-b border-aura-text/5 flex items-center justify-between px-4 sticky top-0 z-[100] rounded-t-[2rem]" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="flex items-center gap-3">
        <button 
          onClick={onMenuClick}
          className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-aura-primary/10 transition-all duration-200 active:scale-90 text-2xl text-aura-primary shadow-sm border border-aura-primary/10"
          title="Open Menu"
        >
          ☰
        </button>
         <div className="flex items-center gap-2">
            <div className="relative">
              <div className={`w-9 h-9 rounded-full bg-gradient-to-br from-aura-primary to-aura-secondary flex items-center justify-center text-sm font-black text-white shadow-sm ${hasDebt ? 'ring-2 ring-red-500' : ''}`}>PK</div>
              {hasDebt && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[9px] font-bold px-1 rounded-full whitespace-nowrap shadow-sm">
                  ${totalOwed}
                </span>
              )}
            </div>
            <div className="flex flex-col">
              <span className="font-black text-lg text-aura-primary tracking-tight">Poker Knights</span>
              <span className="text-[8px] font-medium text-aura-text/40 leading-none mt-0.5">
                {runtimeEnv.isNonProduction && (
                  <span className="font-black text-aura-primary mr-1">{runtimeEnv.appEnv.toUpperCase()}</span>
                )}
                Built: {import.meta.env.VITE_BUILD_TIMESTAMP}
              </span>
            </div>
          </div>

      </div>

      {isAdmin && (
        <div className="text-[10px] px-2.5 py-1 rounded-full bg-aura-primary/10 text-aura-primary font-black uppercase tracking-widest">
          Admin
        </div>
      )}
    </nav>
  );
}

function AdminUserDetailRoute() {
  const { user } = useAuth();
  const { users, loading } = useAdminUsers();
  if (loading) return <LoadingSpinner message="Loading users..." />;
  return <AdminUserDetailPage currentUser={user} users={users} />;
}

function AdminUsersRoute() {
  const { user } = useAuth();
  const { users, loading } = useAdminUsers();
  if (loading) {
    return <LoadingSpinner message="Loading users..." />;
  }
  return <AdminUsersPage currentUser={user} users={users} includeProvisioning />;
}

function AdminOnlyRoute({ allowed, children }: { allowed: boolean; children: React.ReactElement }) {
  return allowed ? children : <Navigate to="/setup" replace />;
}

function BottomTabBar({ activeGame }: { activeGame: Game | null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { games } = useGames();

  // Map Game tab location based on the most recent game by date
  // (not activeGame priority logic, which can prefer an older unsettled game)
  const lastGame = games?.[0] ?? null;

  let gamePath = '/library';
  if (lastGame?.status === 'active') {
    gamePath = '/game';
  } else if (lastGame && ['chip_entry', 'settling'].includes(lastGame.status)) {
    gamePath = `/settlement/${lastGame.id}`;
  } else if (lastGame?.status === 'settled') {
    gamePath = '/game-settled';
  } else if (games && games.length > 0) {
    gamePath = `/settlement/${games[0].id}`;
  } else {
    gamePath = '/library';
  }

  console.log('BottomTabBar gamePath resolved to:', gamePath, { lastGame, gamesCount: games?.length });

    const tabs = [
      { path: '/setup', label: 'Setup', icon: '🏠', activePaths: ['/setup'] },
      { path: gamePath, label: 'Game', icon: '🃏', activePaths: ['/game', '/settlement'] },
      { path: '/entertainment', label: 'Entrt', icon: '🍹', activePaths: ['/entertainment'] },
      { path: '/insights/leaderboard', label: 'Insights', icon: '📊', activePaths: ['/insights'] },
    ];

  return (
    <nav className="min-h-[64px] bg-white/95 backdrop-blur-[10px] border-t border-aura-text/5 flex items-stretch z-[100]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {tabs.map(tab => {
        const active = tab.label === 'Game' 
          ? activeGame?.status === 'active' 
          : tab.activePaths.includes(location.pathname);
        return (
          <button
            key={tab.path}
            onClick={() => navigate(tab.path)}
            className={`flex-1 flex flex-col items-center justify-center gap-1 transition-all duration-200 ${active ? 'bg-aura-accent/15 rounded-2xl scale-95' : 'bg-transparent'} ${tab.label === 'Game' && activeGame?.status === 'active' ? 'relative after:content-[""] after:absolute after:inset-0 after:rounded-2xl after:border-2 after:border-[#006855] after:shadow-[0_0_12px_rgba(0,104,85,0.6)] after:animate-pulse after:pointer-events-none' : ''}`}
          >
            <span className={`text-2xl ${active ? 'grayscale-0' : 'grayscale'}`}>{tab.icon}</span>
            <span className={`text-[10px] font-black uppercase tracking-widest ${active ? 'text-aura-primary' : 'text-[#999999]'}`}>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function DrawerUserAvatar({ user }: { user: { name?: string; photo_url?: string | null; roles?: { app_admin?: boolean }; id?: string } }) {
  const { currentUser } = useUserProfile();
  const src = currentUser?.custom_photo_url || currentUser?.photo_url || user.photo_url || null;
  const displayName = currentUser?.name || user.name || '';
  const isAdmin = user.roles?.app_admin === true;
  const userId = currentUser?.id || user.id;
  const { hasDebt, totalOwed } = useOutstandingDebt(userId);

  return (
    <div className="relative inline-block">
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-aura-primary to-aura-secondary flex items-center justify-center text-xl font-black text-white shadow-md shadow-aura-primary/20 rotate-3 overflow-hidden ${hasDebt ? 'ring-2 ring-red-500' : ''}`}>
        {src ? (
          <img src={src} alt={displayName} className="w-full h-full object-cover" />
        ) : (
          (displayName ?? '?').charAt(0)
        )}
      </div>
      {hasDebt ? (
        <>
          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-50">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
          </div>
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] font-bold px-1 rounded-full whitespace-nowrap shadow-sm z-10">
            ${totalOwed}
          </span>
        </>
      ) : (
        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-50">
          <div className={`w-2.5 h-2.5 rounded-full ${isAdmin ? 'bg-aura-primary' : 'bg-positive-pnl'}`}></div>
        </div>
      )}
    </div>
  );
}

function Drawer({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { config } = useAppConfig();
  const activeChat = config?.telegram_chat_configs?.find(c => c.id === config?.telegram_active_chat_id);
  if (!isOpen) return null;

  const regularItems = [
    { label: 'Chip Scanner', path: '/chip-scanner', icon: '📷' },
    { label: 'Profile', path: '/profile', icon: '👤' },
    { label: 'Library', path: '/library', icon: '📁' },
  ];

  const adminItems = [
    { label: 'Admin Users', path: '/admin/users', icon: '👥' },
    { label: 'Feedback', path: '/admin/feedback', icon: '💬' },
    { label: 'Settings', path: '/settings', icon: '⚙️' },
    { label: 'Data Explorer', path: '/explorer', icon: '🗄️' },
    { label: 'Bot Test', path: '/telegram-test', icon: '🤖' },
  ];

  const handleNav = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSignOut = async () => {
    await signOut();
    onClose();
    navigate('/auth', { replace: true });
  };

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/30 z-[150] backdrop-blur-sm transition-opacity duration-300"
      />
      <div className="fixed top-0 left-0 bottom-0 w-[300px] bg-white z-[200] flex flex-col rounded-r-[2.5rem] shadow-botanical-lg overflow-hidden border-r border-aura-text/5">
        {/* Header Decor */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-aura-primary/5 rounded-full -mr-16 -mt-16 pointer-events-none -z-10" />

        <div className="p-6 flex-1 flex flex-col relative">
          {/* Compact User Profile Section */}
          {user && (
            <div className="mb-16 flex items-center gap-4">
              <DrawerUserAvatar user={user} />
              <div className="min-w-0">
                <h2 className="text-lg font-extrabold text-aura-text leading-tight truncate">{user.name}</h2>
                <p className="text-aura-text/40 text-[9px] font-black uppercase tracking-[0.15em] mt-1 opacity-60">
                  {canManageUsers(user) ? 'Admin Mode' : 'Player Mode'}
                </p>
              </div>
            </div>
          )}

          {/* Scalable Navigation List */}
          <div className="flex-1 space-y-3 overflow-y-auto pr-2 -mr-2">
            {/* Regular items */}
            {regularItems.map((item) => {
              const active = window.location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl transition-all duration-200 active:scale-95 group ${active ? 'bg-aura-primary/10 text-aura-primary' : 'hover:bg-aura-text/5 text-aura-text/60'
                    }`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg shadow-sm group-active:scale-90 transition-transform ${active ? 'bg-white' : 'bg-aura-text/5'
                    }`}>
                    {item.icon}
                  </div>
                  <span className={`text-[14px] ${active ? 'font-extrabold' : 'font-bold'}`}>
                    {item.label}
                  </span>
                </button>
              );
            })}

            {/* Divider + Admin items */}
            {canManageUsers(user) && (
              <>
                <div className="my-3 border-t border-aura-text/10" />
                {adminItems.map((item) => {
                  const active = window.location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNav(item.path)}
                      className={`w-full flex items-center gap-3 p-3.5 rounded-xl transition-all duration-200 active:scale-95 group ${active ? 'bg-aura-primary/10 text-aura-primary' : 'hover:bg-aura-text/5 text-aura-text/60'
                        }`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg shadow-sm group-active:scale-90 transition-transform ${active ? 'bg-white' : 'bg-aura-text/5'
                        }`}>
                        {item.icon}
                      </div>
                      <span className={`text-[14px] ${active ? 'font-extrabold' : 'font-bold'}`}>
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </>
            )}
          </div>

          {/* Telegram Chat Info (admin only) */}
          {canManageUsers(user) && activeChat && (
            <button
              onClick={() => handleNav('/settings?section=telegram')}
              className="w-full mb-4 py-2.5 px-3.5 rounded-xl bg-aura-text/5 text-xs hover:bg-aura-text/10 transition-all duration-200 active:scale-95 text-left"
            >
              <div className="text-[9px] font-black uppercase tracking-[0.15em] text-aura-text/40 mb-1">Telegram Chat</div>
              <div className="font-bold text-aura-text truncate">{activeChat.label}</div>
              <div className="text-[10px] text-aura-text/40 font-mono truncate">{activeChat.chat_id}</div>
            </button>
          )}

          {/* Footer Actions */}
          <div className="mt-auto pt-8 border-t border-aura-text/5 space-y-2">
            <button
              onClick={() => {
                void handleSignOut();
              }}
              className="w-full flex items-center gap-3 p-3.5 rounded-xl hover:bg-error/5 text-error transition-all duration-200 active:scale-95 group"
            >
              <div className="w-9 h-9 rounded-lg bg-error/5 flex items-center justify-center text-lg group-active:scale-90 transition-transform">
                🚪
              </div>
              <span className="text-[14px] font-extrabold">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
